
import React from 'react';
import {
  ShoppingBag,
  Truck,
  CreditCard,
  Store,
  Box,
  ListChecks,
  ArrowRight,
  ShieldCheck,
  Smartphone,
  CheckCircle2,
  Users
} from 'lucide-react';

const OnlineShopAppDetail: React.FC = () => {
  return (
    <div className="pt-24 pb-20 bg-white">
      {/* Hero Section */}
      <section className="container mx-auto px-4 md:px-8 mb-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8 animate-in fade-in slide-in-from-left-8 duration-700">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-100 text-orange-700 rounded-full text-sm font-bold uppercase tracking-wider">
              <Store size={16} /> Solusi Jualan Modern
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 leading-tight">
              Toko Online Anda, <br /> <span className="text-orange-600">Aturan Anda.</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Bangun brand Anda sendiri dengan aplikasi toko online yang didesain untuk kenyamanan pembeli dan kemudahan pengelolaan bagi seller.
            </p>
            <div className="flex flex-wrap gap-4">
              <button className="px-8 py-4 bg-orange-600 text-white rounded-xl font-bold shadow-xl shadow-orange-200 hover:bg-orange-700 hover:shadow-2xl transition-all">
                Mulai Berjualan
              </button>
              <button className="px-8 py-4 bg-white text-slate-700 border border-slate-200 rounded-xl font-bold hover:bg-slate-50 transition-all">
                Coba Demo Pembeli
              </button>
            </div>
          </div>
          <div className="relative">
            <div className="bg-slate-50 rounded-[3rem] p-4 border border-slate-100 shadow-inner overflow-hidden">
               <img src="https://picsum.photos/seed/shop/800/1000" alt="App Display" className="rounded-[2.5rem] shadow-2xl" />
            </div>
            {/* Floating Badges */}
            <div className="absolute top-1/4 -right-8 bg-white p-4 rounded-2xl shadow-xl border border-slate-100 flex items-center gap-3 animate-pulse">
               <div className="bg-orange-100 p-2 rounded-xl text-orange-600"><ShoppingBag size={20} /></div>
               <div>
                  <p className="text-[10px] text-slate-400 font-bold">PESANAN BARU</p>
                  <p className="text-sm font-bold text-slate-900">Rp 1.250.000</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seller Experience - Management */}
      <section className="bg-slate-50 py-24">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
            <h2 className="text-orange-600 font-bold uppercase tracking-wider text-sm">Dashboard Seller</h2>
            <h3 className="text-4xl font-bold text-slate-900">Kelola Bisnis Tanpa Pusing</h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<Box className="text-orange-600" />}
              title="Katalog Produk Instan"
              description="Upload foto, deskripsi, dan harga dalam sekejap. Produk langsung muncul di etalase pembeli."
            />
            <FeatureCard 
              icon={<ListChecks className="text-orange-600" />}
              title="Pantau Pesanan"
              description="Status pesanan dari dibayar, diproses, hingga sampai ke pembeli terpantau dalam satu layar."
            />
            <FeatureCard 
              icon={<Users className="text-orange-600" />}
              title="Manajemen Pelanggan"
              description="Simpan data pembeli setia Anda untuk strategi promosi dan retensi di masa depan."
            />
          </div>
        </div>
      </section>

      {/* Unique Scenarios - Inventory & Pre-order */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 md:order-1">
               <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                     <div className="bg-orange-50 p-6 rounded-3xl border border-orange-100">
                        <h4 className="font-bold text-orange-900 mb-2">Ready Stock</h4>
                        <p className="text-sm text-orange-700">Hubungkan stok gudang langsung ke katalog. Stok habis? Produk otomatis tidak bisa dipesan.</p>
                     </div>
                     <img src="https://picsum.photos/seed/stock/400/400" className="rounded-3xl shadow-lg" alt="Stock" />
                  </div>
                  <div className="space-y-4 pt-8">
                     <img src="https://picsum.photos/seed/preorder/400/400" className="rounded-3xl shadow-lg" alt="Preorder" />
                     <div className="bg-blue-50 p-6 rounded-3xl border border-blue-100">
                        <h4 className="font-bold text-blue-900 mb-2">Sistem Pre-Order</h4>
                        <p className="text-sm text-blue-700">Kumpulkan pesanan dulu sebelum produksi. Cocok untuk produk custom atau catering.</p>
                     </div>
                  </div>
               </div>
            </div>
            <div className="space-y-8 order-1 md:order-2">
              <h2 className="text-3xl font-bold text-slate-900">Skenario Jualan yang Fleksibel</h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Setiap UMKM punya cara jualan yang unik. Itulah mengapa kami menyediakan dua mode jualan utama yang bisa Anda atur per produk.
              </p>
              <ul className="space-y-4">
                 <li className="flex gap-4 items-start">
                    <CheckCircle2 className="text-green-500 shrink-0 mt-1" />
                    <div>
                       <span className="font-bold block">Input Stok Akurat</span>
                       <span className="text-sm text-slate-500">Katalog selalu sinkron dengan sisa barang yang Anda miliki.</span>
                    </div>
                 </li>
                 <li className="flex gap-4 items-start">
                    <CheckCircle2 className="text-green-500 shrink-0 mt-1" />
                    <div>
                       <span className="font-bold block">Skema Pre-Order (PO)</span>
                       <span className="text-sm text-slate-500">Atur batas waktu pemesanan dan estimasi pengiriman dengan jelas.</span>
                    </div>
                 </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Logistics & Payment */}
      <section className="py-24 bg-slate-900 text-white rounded-[4rem] mx-4 md:mx-8 mb-20 overflow-hidden relative">
        <div className="absolute top-0 right-0 p-20 opacity-10">
           <Truck size={300} />
        </div>
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-16">
             <div className="space-y-6">
                <h3 className="text-3xl font-bold">Logistik & Pembayaran <br /><span className="text-orange-400">Tanpa Perantara Mahal</span></h3>
                <p className="text-slate-400">Kami percaya margin keuntungan harus tetap di tangan seller. Kami mempermudah prosesnya tanpa memotong biaya admin yang tinggi.</p>
                <div className="space-y-6 pt-6">
                   <div className="flex gap-4">
                      <div className="bg-white/10 p-3 rounded-2xl"><Truck className="text-orange-400" /></div>
                      <div>
                         <h4 className="font-bold">Pilihan Kurir Terintegrasi</h4>
                         <p className="text-sm text-slate-500">Pembeli bisa memilih kurir favorit mereka. Seller cukup input resi atau gunakan jasa kurir internal.</p>
                      </div>
                   </div>
                   <div className="flex gap-4">
                      <div className="bg-white/10 p-3 rounded-2xl"><CreditCard className="text-orange-400" /></div>
                      <div>
                         <h4 className="font-bold">Pembayaran Langsung ke Seller</h4>
                         <p className="text-sm text-slate-500">Pembeli mentransfer langsung ke nomor rekening yang Anda tentukan. Verifikasi pembayaran manual yang aman dan terpercaya.</p>
                      </div>
                   </div>
                </div>
             </div>
             <div className="bg-white/5 p-8 rounded-[3rem] border border-white/10">
                <h4 className="text-xl font-bold mb-6 flex items-center gap-3">
                   <Smartphone className="text-orange-400" /> Pengalaman Pembeli
                </h4>
                <div className="space-y-4">
                   <div className="p-4 bg-white/10 rounded-2xl flex justify-between items-center">
                      <span className="text-sm">Tampilan Mobile Friendly</span>
                      <ShieldCheck size={18} className="text-green-400" />
                   </div>
                   <div className="p-4 bg-white/10 rounded-2xl flex justify-between items-center">
                      <span className="text-sm">Checkout WhatsApp (Opsional)</span>
                      <ShieldCheck size={18} className="text-green-400" />
                   </div>
                   <div className="p-4 bg-white/10 rounded-2xl flex justify-between items-center">
                      <span className="text-sm">Riwayat Belanja Rapi</span>
                      <ShieldCheck size={18} className="text-green-400" />
                   </div>
                   <div className="pt-6">
                      <button className="w-full py-4 bg-white text-slate-900 rounded-2xl font-bold hover:bg-orange-400 transition-all flex items-center justify-center gap-2">
                         Lihat Live Preview <ArrowRight size={20} />
                      </button>
                   </div>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 text-center">
         <div className="max-w-2xl mx-auto space-y-6">
            <h3 className="text-2xl font-bold">Siap Membuka Toko Online Pertama Anda?</h3>
            <p className="text-slate-500 italic">"DigiMaju Shop membantu saya mengelola ratusan pesanan PO kerupuk setiap minggunya tanpa ada yang terlewat lagi." - Ibu Sari, Owner Sari Kerupuk</p>
            <button className="px-10 py-4 bg-orange-600 text-white rounded-2xl font-bold shadow-lg shadow-orange-100 hover:scale-105 transition-all">
               Buat Akun Seller Gratis
            </button>
         </div>
      </section>
    </div>
  );
};

const FeatureCard: React.FC<{ icon: React.ReactNode, title: string, description: string }> = ({ icon, title, description }) => (
  <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl transition-all group">
    <div className="mb-6 p-4 bg-slate-50 rounded-2xl w-fit group-hover:bg-orange-50 transition-all">
      {icon}
    </div>
    <h4 className="text-xl font-bold text-slate-900 mb-4">{title}</h4>
    <p className="text-slate-500 text-sm leading-relaxed">{description}</p>
  </div>
);

export default OnlineShopAppDetail;
